<template>
  <div
    :class="[classes.badgeButton, !isLeft && classes.badgeButtonReversed]"
    :disabled="disabled"
  >
    <Shard
      :shardDirection="isLeft ? 'left bottom' : 'right bottom'"
      :teamColor="teamColor?.banner.background"
    />

    <TeamBadge
      v-if="useTeamBadges"
      :class="[classes.badge, isLeft ? classes.badgeLeft : classes.badgeRight]"
      :defaultBadge="homeOrAwayTeam"
      :alt="teamName + ' badge'"
      :hideBoxShadow="true"
      :src="badgePath"
      :countryCode="countryCode"
    />

    <TeamJersey
      v-if="useTeamJerseys && teamIconsAvailable"
      :eventId="eventId"
      :class="[classes.badge, isLeft ? classes.badgeLeft : classes.badgeRight]"
      :transform="isLeft ? 'scale(-1, 1)' : undefined"
      :homeOrAwayTeam="homeOrAwayTeam"
      padding="0"
    />

    <Lock
      v-if="suspended"
      :class="[classes.badgeText]"
      :color="lockIconColor"
      width="15"
      height="15"
    />
    <StyledText
      v-else
      tag="span"
      :fontId="buttonStyle?.primaryFont || ''"
      :class="[classes.badgeText]"
    >
      <span :class="classes.badgeTextColor">{{ teamName }}</span>
    </StyledText>
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import {
  BrandContext,
  ButtonStyle,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { defineComponent, computed, PropType } from "vue";
import TeamBadge from "../../../TeamBadge.vue";
import StyledText from "../../../styled/Text.vue";
import Lock from "../../../icons/Lock.vue";
import Shard from "./Shard.vue";
import TeamJersey from "../../../team-jerseys/TeamJerseySelector.vue";
import {
  injectTeamColors,
  injectUseTeamBadges
} from "../../../../lib/teamBranding";
import { injectUseTeamJerseys } from "../../../../lib/teamJerseys";

export default defineComponent({
  name: "StyledTeamBadgeButton",
  components: {
    TeamBadge,
    StyledText,
    Lock,
    Shard,
    TeamJersey
  },
  props: {
    eventId: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonId: {
      type: String,
      required: true
    },
    suspended: {
      type: Boolean,
      required: false,
      default: false
    },
    isLeft: {
      type: Boolean,
      required: false,
      default: true
    },
    teamName: {
      type: String,
      required: true
    },
    badgePath: {
      type: String,
      required: false,
      default: undefined
    },
    countryCode: {
      type: String,
      required: false,
      default: undefined
    },
    isSelected: {
      type: Boolean,
      required: false,
      default: false
    },
    homeOrAwayTeam: {
      type: String as PropType<"home" | "away">,
      default: "home",
      required: true
    }
  },
  setup(props) {
    const brandContext = injectBrand() || ({} as BrandContext);
    const teamColorsContext = injectTeamColors();
    const useTeamBadges = injectUseTeamBadges();
    const useTeamJerseys = injectUseTeamJerseys();

    const teamColor = computed(
      () => teamColorsContext.colors.value[props.homeOrAwayTeam]
    );

    const buttonStyle = computed(() =>
      brandContext?.getButtonStyle(props.buttonId)
    );

    const buttonEnabled = computed(() => !props.disabled);

    const styleComputed = computed(() => ({
      buttonStyle: buttonStyle.value,
      buttonEnabled: buttonEnabled.value,
      primaryColor: brandContext?.getFontStyle(
        buttonStyle?.value?.primaryFont || ""
      )?.color,
      secondaryColor: brandContext?.getFontStyle(
        buttonStyle?.value?.secondaryFont || ""
      )?.color,
      isSelected: props.isSelected
    }));

    const { classesRef } = useStyles(styleComputed);

    const lockIconColor = computed(
      () =>
        brandContext?.getFontStyle(buttonStyle?.value?.primaryFont || "")
          ?.color || "#000"
    );

    const fill = computed(() => {
      if (brandContext?.brand.value?.adTemplate?.team?.useTeamColors) {
        return (
          teamColor.value?.banner.background ||
          brandContext?.teamBranding.value.banner.backgroundColor
        );
      } else {
        return brandContext?.teamBranding.value.banner.backgroundColor;
      }
    });

    const stroke = computed(() => {
      if (brandContext?.brand.value?.adTemplate?.team?.useTeamColors) {
        return (
          teamColor.value?.banner.background ||
          brandContext?.teamBranding.value.banner.backgroundColor
        );
      } else {
        return brandContext?.teamBranding.value.banner.backgroundColor;
      }
    });

    return {
      teamColor,
      teamIconsAvailable: teamColorsContext.teamIconsAvailable,
      useTeamBadges,
      useTeamJerseys,
      classes: classesRef,
      buttonStyle,
      lockIconColor,
      fill,
      stroke
    };
  }
});

const defaultStyling = {
  backgroundColor: "#fff",
  borderRadius: "",
  borderColor: "",
  borderStyle: "",
  borderWidth: "",
  autoInteractions: true
};

const { useStyles } = createUseStyles<
  | "badgeButton"
  | "badgeText"
  | "badgeButtonReversed"
  | "shard"
  | "badge"
  | "badgeLeft"
  | "badgeRight"
  | "badgeTextColor",
  {
    buttonStyle: ButtonStyle;
    buttonEnabled: boolean;
    primaryColor: string;
    secondaryColor: string;
    isSelected: boolean;
  }
>({
  badgeButton: {
    /* justifyContent: props => !!props.suspended, */
    backgroundColor: props =>
      (props.isSelected &&
        props.buttonStyle.actions.selected.backgroundColor) ||
      props.buttonStyle.backgroundColor ||
      defaultStyling.backgroundColor,
    borderRadius: props =>
      props.buttonStyle.borderRadius || defaultStyling.borderRadius,
    borderColor: props =>
      props.buttonStyle.borderColor || defaultStyling.borderColor,
    borderStyle: props =>
      props.buttonStyle.borderStyle || defaultStyling.borderStyle,
    borderWidth: props =>
      props.buttonStyle.borderWidth || defaultStyling.borderWidth,
    "&:hover": {
      backgroundColor: props => {
        return props.isSelected
          ? props.buttonStyle.actions.selected.backgroundColor ||
              props.buttonStyle.backgroundColor ||
              defaultStyling.backgroundColor
          : props.buttonStyle.actions.hover.backgroundColor;
      }
    },
    "&:active": {
      backgroundColor: props =>
        props.isSelected
          ? props.buttonStyle.actions.selected.backgroundColor ||
            props.buttonStyle.backgroundColor ||
            defaultStyling.backgroundColor
          : props.buttonStyle.actions.active.backgroundColor
    },
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "row"
  },
  badgeText: {
    flex: 1,
    fontSize: "12px",
    "$teamTextButton:hover &": {
      color: props => props.buttonStyle.actions.hover.secondaryColor
    },
    "teamTextButton:active &": {
      color: props => props.buttonStyle.actions.active.secondaryColor
    }
  },
  badgeButtonReversed: {
    flexDirection: "row-reverse"
  },
  shard: {
    height: "100%",
    width: "35px"
  },
  badge: {
    zIndex: 0,
    width: "auto",
    height: "100%"
  },
  badgeLeft: {
    position: "absolute",
    left: 0,
    "&.team-jersey": {
      position: "absolute"
    }
  },
  badgeRight: {
    position: "absolute",
    right: 0,
    "&.team-jersey": {
      position: "absolute"
    }
  },
  badgeTextColor: {
    color: props =>
      props.isSelected ? props.buttonStyle.actions.selected.secondaryColor : ""
  }
});
</script>
